import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2 } from "lucide-react";

export default function WorksheetSolver() {
  const { worksheetId } = useParams();
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [ws, setWs] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/worksheets/${worksheetId}`);
      setWs(data);
      if (data.status === "graded") {
        setResult({ score: data.score, max_score: data.max_score, detail: data.detail || {} });
        setAnswers(data.submitted_answers || {});
      }
    })();
  }, [worksheetId]);

  if (!ws) return <div className="text-slate-500">Loading…</div>;
  const readOnly = user?.role !== "student" || !!result;

  const setAns = (id, val) => setAnswers((a) => ({ ...a, [id]: val }));
  const setPair = (id, left, right) => setAnswers((a) => ({ ...a, [id]: { ...(a[id] || {}), [left]: right } }));

  const submit = async () => {
    try {
      const { data } = await api.post(`/worksheets/${worksheetId}/submit`, { answers });
      setResult(data);
      toast.success(`Scored ${data.score}/${data.max_score}!`);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit");
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid={`worksheet-solver-${worksheetId}`}>
      <Link to={user?.role === "student" ? "/student" : "/parent"} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-teal-700"><ArrowLeft size={14} /> Back</Link>
      <div>
        <div className="chip mb-2">Worksheet · {ws.subject}</div>
        <h1 className="font-display text-3xl font-bold text-slate-900">{ws.title}</h1>
        <div className="text-slate-500 text-sm">{ws.items.length} items · {ws.max_score} points</div>
      </div>

      {ws.items.map((it, idx) => {
        const d = result?.detail?.[it.id];
        return (
          <section key={it.id} className={`p-6 rounded-3xl border ${d ? (d.correct ? "bg-teal-50 border-teal-200" : "bg-red-50 border-red-200") : "bg-white border-slate-200"}`}
            data-testid={`solve-item-${idx}`}>
            <div className="text-xs text-slate-500 uppercase tracking-widest">{it.type === "mc" ? "Multiple choice" : it.type === "fill" ? "Fill in" : "Match"} · {it.points} pt</div>
            <div className="mt-1 font-medium text-slate-900">{it.prompt}</div>

            {it.type === "mc" && (
              <div className="mt-3 grid gap-2">
                {(it.choices || []).map((c, ci) => {
                  const sel = String(answers[it.id]) === String(ci);
                  return (
                    <button key={ci} type="button" disabled={readOnly}
                      data-testid={`solve-mc-${idx}-${ci}`}
                      onClick={() => setAns(it.id, String(ci))}
                      className={`text-left px-4 py-3 rounded-xl border ${sel ? "border-teal-400 bg-teal-50" : "border-slate-200 bg-white"} disabled:opacity-70`}>
                      {c}
                    </button>
                  );
                })}
              </div>
            )}

            {it.type === "fill" && (
              <input data-testid={`solve-fill-${idx}`} disabled={readOnly}
                value={answers[it.id] || ""} onChange={(e)=>setAns(it.id, e.target.value)}
                placeholder="Type your answer…"
                className="mt-3 w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-teal-500 outline-none disabled:bg-slate-50" />
            )}

            {it.type === "match" && (
              <div className="mt-3 space-y-2">
                {(it.pairs || []).map((p, pi) => (
                  <div key={pi} className="grid grid-cols-2 gap-2 items-center">
                    <div className="px-3 py-2 rounded-lg bg-slate-50 border border-slate-200">{p.left}</div>
                    <select data-testid={`solve-match-${idx}-${pi}`} disabled={readOnly}
                      value={(answers[it.id] || {})[p.left] || ""}
                      onChange={(e)=>setPair(it.id, p.left, e.target.value)}
                      className="px-3 py-2 rounded-lg border border-slate-200 bg-white">
                      <option value="">Choose…</option>
                      {[...new Set((it.pairs || []).map((x) => x.right))].map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            )}

            {d && (
              <div className={`mt-3 text-sm font-semibold ${d.correct ? "text-teal-800" : "text-red-800"}`}>
                {d.correct ? "Correct" : "Not quite"}
              </div>
            )}
          </section>
        );
      })}

      {result ? (
        <div className="p-6 rounded-3xl bg-teal-50 border border-teal-200 text-center" data-testid="worksheet-result">
          <CheckCircle2 className="mx-auto text-teal-700" size={40} />
          <div className="mt-2 font-display text-2xl font-bold text-slate-900">{result.score} / {result.max_score}</div>
          <button onClick={()=>nav("/student")} className="mt-4 px-5 py-2 rounded-full bg-teal-700 hover:bg-teal-800 text-white text-sm font-semibold">Back to today</button>
        </div>
      ) : (
        user?.role === "student" && (
          <button data-testid="worksheet-submit" onClick={submit} className="w-full py-4 rounded-full bg-teal-700 hover:bg-teal-800 text-white font-semibold">
            Submit worksheet
          </button>
        )
      )}
    </div>
  );
}
